import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, InputAdornment } from "@mui/material";
import { CategoryService, GonogoInformationService } from "../../../../api";
import { useApi } from "../../../../common/hooks";
import { translate } from "../../../../common/providers";
import { debounce, getCheckedCategoriesCount, getParentsIdsList, isNonEmptyArray } from "../../../../common/utils";
import { setDisableKeyEvents } from "../../../../common/slice";
import { setExpandedIds } from "../../../my-review/slice/project-document/project-document-slice";
import styles from "./CategoriesTreeView.module.css";
import {
	CategoryList,
	CustomIconButton,
	CustomTextField,
	IconComponent,
	ViewTabs,
	icon,
} from "../../../../common/components";

const extractCheckedIds = (categories) => {
	let ids = [];
	categories.forEach((c) => {
		if (c.matched) {
			ids.push(c.id);
		}
		if (Array.isArray(c.subThematic) && c.subThematic.length > 0) {
			ids = ids.concat(extractCheckedIds(c.subThematic));
		}
	});
	return ids;
};

export default function CategoriesTreeView({ deliverableId, onCategoriesChange }) {
	const { call: getByDeliverable } = useApi(CategoryService.getByDeliverable);
	const { call: attachInformationCategory } = useApi(GonogoInformationService.attachInformationCategory);
	const [checkedIds, setCheckedIds] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [checkedCounts, setCheckedCounts] = useState({});
	const [showSelected, setShowSelected] = useState(false);
	const [categorySearch, setCategorySearch] = useState("");
	const [categories, setCategories] = useState([]);
	const [categorySearchDebounced, setCategorySearchDebounced] = useState("");
	const [selectedTab, setSelectedTab] = useState(0);
	const [attachedCategoryIds, setAttachedCategoryIds] = useState([]);
	const projectId = useSelector(({ context }) => context.project.id);
	const expandedIds = useSelector(({ srProjectDocument }) => srProjectDocument.expandedIds);
	const disableKeyEvents = useSelector(({ context }) => context.disableKeyEvents);
	const dispatch = useDispatch();
	const stableEmptyArray = useMemo(() => [], []);
	const debouncedSetCategorySearch = useRef(
		debounce((value) => {
			setIsLoading(false);
			setCategorySearchDebounced(value);
		}, 300)
	);

	const loadCategories = useCallback(
		(search) => {
			if (deliverableId) {
				setIsLoading(true);
				getByDeliverable({ projectId, deliverableId }, { search })
					.then((data) => {
						setCategories(data);
						setCheckedIds(extractCheckedIds(data));
						setIsLoading(false);
					})
					.catch((err) => {
						setIsLoading(false);
						console.error(err);
					});
			}
		},
		[deliverableId, getByDeliverable, projectId]
	);

	useEffect(() => {
		loadCategories(categorySearchDebounced);
	}, [loadCategories, categorySearchDebounced]);

	useEffect(() => {
		if (!categorySearch) {
			setCheckedCounts(getCheckedCategoriesCount(categories, checkedIds));
		}
	}, [checkedIds, categories, categorySearch]);

	useEffect(
		() => () => {
			if (disableKeyEvents) {
				dispatch(setDisableKeyEvents(false));
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	useEffect(() => {
		if (onCategoriesChange) {
			onCategoriesChange(checkedIds);
		}
	}, [checkedIds, onCategoriesChange]);

	const handleResetSearchText = useCallback(() => {
		setCategorySearch("");
		setCategorySearchDebounced("");
		setIsLoading(false);
	}, []);

	const handleChangeSearchText = useCallback(
		(event) => {
			const { value } = event.target;
			if (categorySearch.length === 0 && value.length > 0 && showSelected) {
				setShowSelected(false);
			}
			setCategorySearch(value);
			setIsLoading(true);
			debouncedSetCategorySearch.current(value);
		},
		[categorySearch, showSelected]
	);

	const handleChangeShowSelected = useCallback((_, tab) => {
		setShowSelected((prev) => !prev);
		setSelectedTab(tab);
	}, []);

	const getChildrenIdsList = useCallback((category) => {
		if (!category) {
			return [];
		}
		if (!Array.isArray(category.subThematic)) {
			return [category.id];
		}
		return [category.id, ...category.subThematic.map((cat) => getChildrenIdsList(cat))].flat();
	}, []);

	const handleCheck = useCallback(
		(checked, checkedCategory) => {
			setCheckedIds((prev) => {
				if (checked) {
					if (prev.includes(checkedCategory.id)) {
						return prev;
					}
					return [...prev, checkedCategory.id];
				}
				return prev.filter((id) => id !== checkedCategory.id);
			});

			setTimeout(() => {
				if (checked) {
					const parents = getParentsIdsList(categories, checkedCategory);
					if (isNonEmptyArray(parents)) {
						setCheckedIds((prev) => Array.from(new Set(prev.concat(parents))));
					}
				} else {
					const children = getChildrenIdsList(checkedCategory);
					if (isNonEmptyArray(children)) {
						setCheckedIds((prev) => prev.filter((id) => !children.includes(id)));
						setAttachedCategoryIds((prev) => prev.filter((p) => !children.includes(p)));
					}
				}
			}, 0);
		},
		[categories, getChildrenIdsList]
	);

	const handleExpand = useCallback(
		(expanded, category) => {
			if (!expandedIds.includes(category.id)) {
				dispatch(setExpandedIds([...expandedIds, category.id]));
			} else {
				dispatch(setExpandedIds(expandedIds.filter((item) => item !== category.id)));
			}
		},
		[dispatch, expandedIds]
	);

	const handleAttachInformationCategory = useCallback(
		(categoryId, attached) => {
			attachInformationCategory({ categoryId, deliverableId }, !attached)
				.then(() => {
					if (attached) {
						setAttachedCategoryIds((prev) => prev.filter((p) => p !== categoryId));
					} else {
						setAttachedCategoryIds((prev) => [...prev, categoryId]);
					}
				})
				.catch(console.error);
		},
		[attachInformationCategory, deliverableId]
	);

	const isNotEmpty = useMemo(
		() => isNonEmptyArray(checkedIds) || (selectedTab === 0 && isNonEmptyArray(categories)),
		[checkedIds, selectedTab, categories]
	);

	return (
		<div className={styles.main}>
			<div className={styles.container}>
				<>
					<div className={styles.container_filter}>
						<div>
							<ViewTabs
								className={styles.tabs}
								selectedTab={selectedTab}
								tabs={[
									{
										label: translate("deliverables-list.edit.categories.all"),
									},
									{
										label: translate("deliverables-list.edit.categories.selected", {
											checkedCount: checkedCounts || 0,
										}),
										disabled: Number(checkedCounts) === 0,
									},
								]}
								onChange={handleChangeShowSelected}
							/>
						</div>
					</div>
					<div className={styles.container_search}>
						<CustomTextField
							autoFocus
							className={styles.search}
							InputProps={{
								className: styles.textField,
								endAdornment: (
									<InputAdornment position="end">
										{isLoading && <CircularProgress size={25} thickness={3} />}
										{!isLoading && !categorySearch && <IconComponent icon={icon.faSearch} />}
										{!isLoading && categorySearch && (
											<CustomIconButton
												icon={icon.faTimesCircleRegular}
												onClick={handleResetSearchText}
											/>
										)}
									</InputAdornment>
								),
							}}
							placeholder={translate("deliverables-list.edit.categories.search")}
							size="small"
							value={categorySearch}
							variant="outlined"
							onBlur={() => dispatch(setDisableKeyEvents(false))}
							onChange={handleChangeSearchText}
							onFocus={() => dispatch(setDisableKeyEvents(true))}
						/>
					</div>
				</>
				<div className={`${styles["container_list--fullHeight"]} ${isLoading ? styles.loadingContent : ""}`}>
					{(isNotEmpty && (
						<CategoryList
							editable
							attachedCategoryIds={attachedCategoryIds}
							categoryList={categories}
							checkedIds={checkedIds}
							deliverableId={deliverableId}
							disabledIds={stableEmptyArray}
							disableEditing={false}
							expandedIds={expandedIds}
							forceExpanded={selectedTab === 1 || selectedTab === 0}
							showSelected={showSelected}
							onAttachInformationCategory={handleAttachInformationCategory}
							onCheck={handleCheck}
							onExpand={handleExpand}
						/>
					)) || (
						<div className={styles.emptyState}>
							<IconComponent
								className={styles.emptyStateIcon}
								color="var(--blue-500)"
								icon={icon.faInfoCircle}
							/>
							{translate("deliverables-list.edit.categories.empty-state")}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
